/**
 * Role-contextual contributor navigation profiles (Build Contract §1D).
 *
 * Each job role resolves to a focused or operational shell and a default entry path.
 * DB role_permissions remain authoritative for what links are actionable; profiles
 * only shape which surface is default and how nav is ordered.
 */

import {
  DEFAULT_JOB_ROLES,
  ELEVATED_JOB_ROLE_SLUGS,
  type JobRoleDefinition,
} from '../types/prioritization/jobRoles';
import type { PermissionAction } from '../types/prioritization/permissions';

export type ContributorShellMode = 'focused' | 'operational';

export type ContributorNavSection = 'myWork' | 'outputs' | 'workspace';

/** Sections of the role-based menu inside Plans (/planning/*). */
export type PlansRoleMenuSection = 'commitment' | 'capabilities' | 'peopleStructure' | 'planOperations';

export type ContributorMyWorkItem = 'sessions' | 'tasks' | 'exchange';

export interface ContributorRoleNavItem {
  label: string;
  href: string;
  /**
   * The person must hold at least one of these capabilities for the link to be
   * drawn (build contract §2.8 — never draw a control the person may not use).
   * Taken from the role's own permission list, so holding the role normally
   * satisfies it; a workspace that trimmed the role's permissions hides it.
   */
  requires?: PermissionAction[];
  /** Only drawn where Plans is offered (not in a personal space). */
  needsPlans?: boolean;
}

/**
 * Settings on the role-based menu opens the person's own settings (My Account:
 * My roles, profile, feature toggles …). Before 2026-10-14 it opened the
 * feature toggles directly, which left My roles out of reach.
 */
export const ROLE_MENU_SETTINGS_HREF = '/prioritization/settings/my-account';

/** The Plan of Record page (Plans → Plan of Record). */
export const PLAN_OF_RECORD_HREF = '/planning/plans';

export interface ContributorNavProfile {
  /** Primary job slug this profile represents, or 'workspace-member' for baseline */
  primaryJob: string;
  shell: ContributorShellMode;
  defaultPath: string;
  sectionOrder: ContributorNavSection[];
  myWorkOrder: ContributorMyWorkItem[];
  /** Operational jobs: extra links surfaced in MY WORK (e.g. Cycles for cycle-designer). */
  roleNavItems?: ContributorRoleNavItem[];
}

const OPERATIONAL_JOB_SET = new Set<string>(ELEVATED_JOB_ROLE_SLUGS);

/**
 * Order of job roles, used for the order of links in the role-based menu and
 * for which role picks the landing page and the role label.
 *
 * Working roles come first (the ones that run the work: facilitator, track
 * owner, cycle designer, …), then the narrow ones (decision authority,
 * approver, participant, …). Before 2026-10-14 the narrow roles came first,
 * which contradicted this rule and, because the first role held also chose the
 * whole menu, hid a facilitator's or track owner's links behind a narrow role.
 * The menu is now the union of every active role (see `buildRoleMenu`), so this
 * order only decides link order, never which links appear.
 */
export const JOB_PROFILE_PRIORITY: string[] = [
  // Working roles (ELEVATED_JOB_ROLE_SLUGS)
  'session-facilitator',
  'track-owner',
  'cycle-designer',
  'local-cycle-designer',
  'session-designer',
  'priority-list-owner',
  'plan-of-record-manager',
  'item-triage',
  'item-manager',
  'item-selection-authority',
  'tool-selection-authority',
  'criteria-authority',
  'criteria-value-authority',
  'values-approver',
  'relationship-curator',
  'workflow-designer',
  // Narrow roles
  'decision-authority',
  'priority-approver',
  'priority-setter',
  'selection-authority',
  'ball-holder',
  'session-participant',
  'catchball-contributor',
  'values-collector',
  'reports-analyst',
  'strategist',
  'idea-submitter',
];

const JOB_PROFILES: Record<string, Omit<ContributorNavProfile, 'primaryJob'>> = {
  'session-participant': {
    shell: 'focused',
    defaultPath: '/prioritization/sessions',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['sessions', 'tasks'],
  },
  'session-facilitator': {
    shell: 'operational',
    defaultPath: '/prioritization/sessions',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['sessions', 'tasks'],
    roleNavItems: [{ label: 'Facilitate Sessions', href: '/prioritization/sessions', requires: ['session.facilitate'] }],
  },
  'session-designer': {
    shell: 'operational',
    defaultPath: '/prioritization/sessions',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['sessions', 'tasks'],
    roleNavItems: [{ label: 'Session Setup', href: '/prioritization/sessions', requires: ['session.pool.add', 'tracks.configure'] }],
  },
  'tool-selection-authority': {
    shell: 'operational',
    defaultPath: '/prioritization/sessions',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['sessions', 'tasks'],
    roleNavItems: [{ label: 'Session Tools', href: '/prioritization/sessions/tools', requires: ['session.tools.select', 'session.tools.configure'] }],
  },
  'ball-holder': {
    shell: 'focused',
    defaultPath: '/prioritization/catchball',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['exchange', 'sessions', 'tasks'],
  },
  'decision-authority': {
    shell: 'focused',
    defaultPath: '/prioritization/cascade/executive-inbox',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['exchange', 'tasks'],
  },
  'catchball-contributor': {
    shell: 'focused',
    defaultPath: '/prioritization/catchball',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['exchange'],
  },
  'selection-authority': {
    shell: 'focused',
    defaultPath: '/prioritization/catchball',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['exchange', 'tasks'],
  },
  'priority-setter': {
    shell: 'focused',
    defaultPath: '/prioritization/lists',
    sectionOrder: ['outputs', 'myWork', 'workspace'],
    myWorkOrder: ['exchange', 'tasks'],
  },
  'priority-approver': {
    shell: 'focused',
    defaultPath: '/prioritization/lists',
    sectionOrder: ['outputs', 'myWork', 'workspace'],
    myWorkOrder: ['exchange', 'tasks'],
  },
  'cycle-designer': {
    shell: 'operational',
    defaultPath: '/prioritization/cycles',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['sessions', 'tasks'],
    roleNavItems: [{ label: 'Cycles', href: '/prioritization/cycles', requires: ['process.create', 'process.edit'] }],
  },
  'local-cycle-designer': {
    shell: 'operational',
    defaultPath: '/prioritization/cycles',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['sessions', 'tasks'],
    roleNavItems: [{ label: 'Cycles', href: '/prioritization/cycles', requires: ['process.create', 'process.edit'] }],
  },
  'track-owner': {
    shell: 'operational',
    defaultPath: '/prioritization/tracks',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['sessions', 'tasks'],
    roleNavItems: [{ label: 'Tracks', href: '/prioritization/tracks', requires: ['tracks.configure', 'track.set_official_result'] }],
  },
  'priority-list-owner': {
    shell: 'operational',
    defaultPath: '/prioritization/lists',
    sectionOrder: ['outputs', 'myWork', 'workspace'],
    myWorkOrder: ['sessions', 'tasks'],
    roleNavItems: [{ label: 'Item Pools', href: '/prioritization/lists', requires: ['lists.create', 'lists.edit'] }],
  },
  'item-triage': {
    shell: 'operational',
    defaultPath: '/prioritization/intake/triage',
    sectionOrder: ['workspace', 'myWork', 'outputs'],
    myWorkOrder: ['tasks'],
    roleNavItems: [{ label: 'Triage Queue', href: '/prioritization/intake/triage', requires: ['items.triage'] }],
  },
  'idea-submitter': {
    shell: 'focused',
    defaultPath: '/prioritization/intake/submit',
    sectionOrder: ['workspace', 'myWork', 'outputs'],
    myWorkOrder: ['tasks'],
  },
  'item-manager': {
    shell: 'operational',
    defaultPath: '/prioritization/items',
    sectionOrder: ['workspace', 'myWork', 'outputs'],
    myWorkOrder: ['sessions', 'tasks'],
    roleNavItems: [{ label: 'Item Backlog', href: '/prioritization/items', requires: ['items.edit', 'items.create'] }],
  },
  'item-selection-authority': {
    shell: 'operational',
    defaultPath: '/prioritization/items',
    sectionOrder: ['workspace', 'myWork', 'outputs'],
    myWorkOrder: ['tasks'],
    roleNavItems: [{ label: 'Item Selection', href: '/prioritization/items', requires: ['items.select'] }],
  },
  'values-collector': {
    shell: 'focused',
    defaultPath: '/prioritization/sessions',
    sectionOrder: ['myWork', 'outputs', 'workspace'],
    myWorkOrder: ['sessions', 'tasks'],
  },
  'criteria-authority': {
    shell: 'operational',
    defaultPath: '/prioritization/items',
    sectionOrder: ['workspace', 'myWork', 'outputs'],
    myWorkOrder: ['sessions'],
    roleNavItems: [{ label: 'Scoring', href: '/prioritization/scoring', requires: ['criteria.edit', 'values.define_scales', 'values.accept'] }],
  },
  'criteria-value-authority': {
    shell: 'operational',
    defaultPath: '/prioritization/scoring',
    sectionOrder: ['workspace', 'myWork', 'outputs'],
    myWorkOrder: ['sessions'],
    roleNavItems: [{ label: 'Scoring', href: '/prioritization/scoring', requires: ['criteria.edit', 'values.define_scales', 'values.accept'] }],
  },
  'values-approver': {
    shell: 'operational',
    defaultPath: '/prioritization/scoring',
    sectionOrder: ['workspace', 'myWork', 'outputs'],
    myWorkOrder: ['sessions'],
    roleNavItems: [{ label: 'Scoring', href: '/prioritization/scoring', requires: ['criteria.edit', 'values.define_scales', 'values.accept'] }],
  },
  'relationship-curator': {
    shell: 'operational',
    defaultPath: '/prioritization/relationships',
    sectionOrder: ['workspace', 'myWork', 'outputs'],
    myWorkOrder: ['tasks'],
    roleNavItems: [{ label: 'Relationships', href: '/prioritization/relationships', requires: ['relationships.create', 'relationships.edit'] }],
  },
  'plan-of-record-manager': {
    shell: 'operational',
    // The Plan of Record lives under Plans (/planning/plans). Before
    // 2026-10-14 this link was named Plan of Record and opened Reports.
    defaultPath: PLAN_OF_RECORD_HREF,
    sectionOrder: ['outputs', 'myWork', 'workspace'],
    myWorkOrder: ['tasks'],
    roleNavItems: [{ label: 'Plan of Record', href: PLAN_OF_RECORD_HREF, needsPlans: true }],
  },
  'reports-analyst': {
    shell: 'focused',
    defaultPath: '/prioritization/reports',
    sectionOrder: ['outputs', 'myWork', 'workspace'],
    myWorkOrder: ['tasks'],
  },
  'strategist': {
    shell: 'focused',
    defaultPath: '/prioritization/reports',
    sectionOrder: ['outputs', 'workspace', 'myWork'],
    myWorkOrder: ['tasks'],
  },
  'workflow-designer': {
    shell: 'operational',
    defaultPath: '/prioritization/settings/workflows',
    sectionOrder: ['workspace', 'myWork', 'outputs'],
    myWorkOrder: ['tasks'],
    roleNavItems: [{ label: 'Workflows', href: '/prioritization/settings/workflows', requires: ['workflow.create', 'workflow.edit'] }],
  },
};

const BASELINE_PROFILE: ContributorNavProfile = {
  primaryJob: 'workspace-member',
  shell: 'focused',
  defaultPath: '/prioritization/sessions',
  sectionOrder: ['myWork', 'outputs', 'workspace'],
  myWorkOrder: ['sessions', 'tasks'],
};

const JOB_ROLE_SLUGS = new Set(DEFAULT_JOB_ROLES.map((r) => r.slug));

export function jobSlugsFromAssignments(roleNames: string[]): string[] {
  return roleNames.filter((n) => n !== 'workspace-member' && JOB_ROLE_SLUGS.has(n));
}

export function usesOperationalShell(roleNames: string[]): boolean {
  return jobSlugsFromAssignments(roleNames).some((slug) => OPERATIONAL_JOB_SET.has(slug));
}

/**
 * Resolve the role-contextual nav profile for the current assignment set.
 * Operational jobs always win over focused jobs when both are present.
 */
/** Sort job slugs by profile priority (highest priority first). */
export function sortJobSlugsByProfilePriority(jobSlugs: string[]): string[] {
  const set = new Set(jobSlugs);
  const ordered = JOB_PROFILE_PRIORITY.filter((slug) => set.has(slug));
  for (const slug of jobSlugs) {
    if (!ordered.includes(slug)) ordered.push(slug);
  }
  return ordered;
}

export function resolveContributorNavProfile(roleNames: string[]): ContributorNavProfile {
  const jobSlugs = jobSlugsFromAssignments(roleNames);

  for (const slug of JOB_PROFILE_PRIORITY) {
    if (!jobSlugs.includes(slug)) continue;
    const template = JOB_PROFILES[slug];
    if (template) {
      return { primaryJob: slug, ...template };
    }
  }

  return { ...BASELINE_PROFILE };
}

/** All job slugs that ship in migrations 521/531 — keep in sync with DB seed. */
export const SHIPPING_JOB_ROLE_SLUGS: readonly string[] = DEFAULT_JOB_ROLES.map((r) => r.slug);

export function getJobRoleDefinition(slug: string): JobRoleDefinition | undefined {
  return DEFAULT_JOB_ROLES.find((r) => r.slug === slug);
}

/** Nav profile for a single job slug (explicit profile or workspace-member baseline). */
export function getContributorNavProfileForJobSlug(slug: string): ContributorNavProfile {
  const template = JOB_PROFILES[slug];
  if (template) return { primaryJob: slug, ...template };
  return { ...BASELINE_PROFILE, primaryJob: slug };
}

export function getWorkspaceMemberNavProfile(): ContributorNavProfile {
  return { ...BASELINE_PROFILE };
}

// ─── Role-based menu (the short side menu for members who are not admins) ────

/**
 * The job roles that shape what a person sees: the roles they chose to feature
 * (`user_rich_profiles.featured_job_role_slugs`), or every job role they hold
 * when none is featured. Featured slugs the person no longer holds are ignored.
 * Ordered by JOB_PROFILE_PRIORITY. Build contract §1D, "Featured roles".
 */
export function resolveActiveJobSlugs(
  assignedRoleNames: string[],
  featuredJobRoleSlugs: string[] = [],
): string[] {
  const assigned = jobSlugsFromAssignments(assignedRoleNames);
  if (assigned.length === 0) return [];
  const featured = featuredJobRoleSlugs.filter((slug) => assigned.includes(slug));
  return sortJobSlugsByProfilePriority(featured.length > 0 ? featured : assigned);
}

export type RoleMenuIcon =
  | 'sessions'
  | 'tasks'
  | 'exchange'
  | 'results'
  | 'reports'
  | 'plan'
  | 'items'
  | 'submit'
  | 'cycles'
  | 'apps';

export interface RoleMenuLink {
  label: string;
  href: string;
  icon: RoleMenuIcon;
  /** The Sessions link carries the "needs your input" count. */
  showsSessionCount?: boolean;
}

export interface RoleMenuSection {
  id: ContributorNavSection | PlansRoleMenuSection;
  label: string;
  links: RoleMenuLink[];
}

export interface RoleMenu {
  /** The roles the menu was built from (featured, or all held), in order. */
  roleSlugs: string[];
  sections: RoleMenuSection[];
  /** Draw the Priorities ↔ Plans switch. */
  showProductSwitch: boolean;
}

export interface RoleMenuInput {
  assignedRoleNames: string[];
  featuredJobRoleSlugs?: string[];
  /** True when the person holds at least one of the capabilities. */
  canAny: (actions: PermissionAction[]) => boolean;
  /** The person has at least one app they may open. */
  hasApps: boolean;
  /** Plans (and the Plan of Record) are offered here — false in a personal space. */
  plansAvailable: boolean;
  /**
   * Which product's side menu: Priorities (default) or Plans (/planning/*).
   * Both come out of this one builder so the two menus follow the same roles
   * and the same "only what you may use" rule.
   */
  product?: 'priorities' | 'plans';
  /** Plans only: Launch Planning is switched on for this workspace. */
  launchPlanningAvailable?: boolean;
}

const SECTION_LABELS: Record<ContributorNavSection, string> = {
  myWork: 'My Work',
  outputs: 'Outputs',
  workspace: 'Workspace',
};

const EXECUTIVE_INBOX_HREF = '/prioritization/cascade/executive-inbox';
const CASCADE_HREF = '/prioritization/catchball';
const SESSIONS_HREF = '/prioritization/sessions';

function roleLinkIcon(href: string): RoleMenuIcon {
  if (href.includes('/cycles')) return 'cycles';
  if (href === PLAN_OF_RECORD_HREF) return 'plan';
  return 'sessions';
}

/**
 * Plans links on the role-based menu. There are no Plans-specific permissions
 * yet, so each link is decided by the nearest capability the person holds:
 *  - Home and the Plan of Record: everyone (reading the official plan).
 *  - Plan operations (Milestones, Governance, Assumptions & Risks, Changes,
 *    Work Graph): people who edit cycles or watch drift from the plan
 *    (cycle designers, the Plan of Record manager).
 *  - Building a plan (Viability, Launch Planning, People & Capacity, Areas,
 *    Plan Workbench): people who create or edit cycles.
 * Admins, and everyone when the workspace switch is off, keep the full Plans menu.
 */
const PLANS_OPERATE: PermissionAction[] = ['process.edit', 'drift.monitor', 'drift.flag'];
const PLANS_BUILD: PermissionAction[] = ['process.create', 'process.edit'];
export const PLANS_HOME_HREF = '/planning';
export const PLANS_LAUNCH_HREF = '/planning/launch-calendar';

interface PlansRoleMenuEntry {
  section: PlansRoleMenuSection;
  label: string;
  href: string;
  requires?: PermissionAction[];
}

const PLANS_ROLE_MENU_LINKS: PlansRoleMenuEntry[] = [
  { section: 'commitment', label: 'Home', href: PLANS_HOME_HREF },
  { section: 'commitment', label: 'Plan of Record', href: PLAN_OF_RECORD_HREF },
  { section: 'capabilities', label: 'Viability', href: '/planning/engine', requires: PLANS_BUILD },
  { section: 'capabilities', label: 'Launch Planning', href: PLANS_LAUNCH_HREF, requires: PLANS_BUILD },
  { section: 'peopleStructure', label: 'People & Capacity', href: '/planning/people-capacity', requires: PLANS_BUILD },
  { section: 'peopleStructure', label: 'Areas', href: '/planning/areas', requires: PLANS_BUILD },
  { section: 'peopleStructure', label: 'Milestones', href: '/planning/milestones', requires: PLANS_OPERATE },
  { section: 'planOperations', label: 'Governance', href: '/planning/governance', requires: PLANS_OPERATE },
  { section: 'planOperations', label: 'Assumptions & Risks', href: '/planning/assumptions-risks', requires: PLANS_OPERATE },
  { section: 'planOperations', label: 'Changes', href: '/planning/changes', requires: PLANS_OPERATE },
  { section: 'planOperations', label: 'Work Graph', href: '/planning/work-graph', requires: PLANS_OPERATE },
  { section: 'planOperations', label: 'Plan Workbench', href: '/planning/spreadsheet', requires: PLANS_BUILD },
];

const PLANS_SECTION_LABELS: Record<PlansRoleMenuSection, string> = {
  commitment: 'Commitment',
  capabilities: 'Capabilities',
  peopleStructure: 'People & structure',
  planOperations: 'Plan operations',
};

/** True when the role-based menu carries a link to this address. */
export function roleMenuHasLink(menu: RoleMenu, href: string): boolean {
  return menu.sections.some((section) => section.links.some((link) => link.href === href));
}

function buildPlansRoleMenu(input: RoleMenuInput, roleSlugs: string[]): RoleMenu {
  const sections: RoleMenuSection[] = [];
  for (const entry of PLANS_ROLE_MENU_LINKS) {
    if (entry.href === PLANS_LAUNCH_HREF && !input.launchPlanningAvailable) continue;
    if (entry.requires && !input.canAny(entry.requires)) continue;
    let section = sections.find((s) => s.id === entry.section);
    if (!section) {
      section = { id: entry.section, label: PLANS_SECTION_LABELS[entry.section], links: [] };
      sections.push(section);
    }
    section.links.push({ label: entry.label, href: entry.href, icon: 'plan' });
  }
  // The switch back to Priorities is always there inside Plans.
  return { roleSlugs, sections, showProductSwitch: true };
}

/**
 * The one builder of the role-based menu. The desktop short side menu
 * (ContributorSidebar) and the phone drawer (MobileShell) both draw this list,
 * so the two can never disagree.
 *
 * The menu is the UNION of every active role's links (featured roles, or all
 * held roles when none is featured) — a person with several roles gets the
 * links of all of them, not the menu of one. JOB_PROFILE_PRIORITY only orders
 * the links and sections. Every link is drawn only when the person may use it.
 *
 * With `product: 'plans'` it builds the Plans side menu instead (PlanningSidebar
 * and the phone's Plans drawer), from PLANS_ROLE_MENU_LINKS.
 */
export function buildRoleMenu(input: RoleMenuInput): RoleMenu {
  const roleSlugs = resolveActiveJobSlugs(input.assignedRoleNames, input.featuredJobRoleSlugs);
  if (input.product === 'plans') return buildPlansRoleMenu(input, roleSlugs);
  const profiles = roleSlugs.length > 0
    ? roleSlugs.map((slug) => getContributorNavProfileForJobSlug(slug))
    : [getWorkspaceMemberNavProfile()];
  const { canAny, plansAvailable } = input;

  const seen = new Set<string>();
  const take = (link: RoleMenuLink, into: RoleMenuLink[]) => {
    if (seen.has(link.href)) return;
    seen.add(link.href);
    into.push(link);
  };

  // My Work — each role's own links first, then the shared work links.
  const myWork: RoleMenuLink[] = [];
  for (const profile of profiles) {
    for (const item of profile.roleNavItems ?? []) {
      if (item.needsPlans && !plansAvailable) continue;
      if (item.requires && !canAny(item.requires)) continue;
      take({
        label: item.label,
        href: item.href,
        icon: roleLinkIcon(item.href),
        // A role link to the sessions page stands in for the Sessions link, so it carries its count.
        ...(item.href === SESSIONS_HREF ? { showsSessionCount: true } : {}),
      }, myWork);
    }
  }
  const showSessions = canAny(['session.view', 'session.participate']);
  const showTasks = canAny(['task.self_assign', 'task.assign']);
  const showCascade = canAny(['catchball.propose', 'catchball.request', 'catchball.return']);
  // The Executive Inbox is where a decision authority accepts or rejects what comes up.
  const showExecutiveInbox = canAny(['catchball.accept', 'catchball.reject']);
  for (const profile of profiles) {
    for (const item of profile.myWorkOrder) {
      if (item === 'sessions' && showSessions) {
        take({ label: 'Sessions', href: SESSIONS_HREF, icon: 'sessions', showsSessionCount: true }, myWork);
      } else if (item === 'tasks' && showTasks) {
        take({ label: 'My work', href: '/prioritization/my-work', icon: 'tasks' }, myWork);
      } else if (item === 'exchange') {
        if (profile.primaryJob === 'decision-authority') {
          if (showExecutiveInbox) take({ label: 'Executive Inbox', href: EXECUTIVE_INBOX_HREF, icon: 'exchange' }, myWork);
        } else if (showCascade) {
          take({ label: 'Cascade', href: CASCADE_HREF, icon: 'exchange' }, myWork);
        }
      }
    }
  }

  // Outputs — the Plan of Record is the official output of the work.
  const outputs: RoleMenuLink[] = [];
  if (canAny(['session.view_results', 'analytics.view', 'lists.view'])) {
    take({ label: 'Results', href: '/prioritization/scoreboard', icon: 'results' }, outputs);
  }
  if (canAny(['analytics.view'])) {
    take({ label: 'Reports', href: '/prioritization/reports', icon: 'reports' }, outputs);
  }
  if (plansAvailable) {
    take({ label: 'Plan of Record', href: PLAN_OF_RECORD_HREF, icon: 'plan' }, outputs);
  }

  // Workspace
  const workspace: RoleMenuLink[] = [];
  if (canAny(['items.view'])) take({ label: 'Items', href: '/prioritization/items', icon: 'items' }, workspace);
  if (canAny(['items.create'])) {
    take({ label: 'Submit an idea', href: '/prioritization/intake/submit', icon: 'submit' }, workspace);
  }
  if (input.hasApps) take({ label: 'Apps', href: '/prioritization/apps', icon: 'apps' }, workspace);

  const linksBySection: Record<ContributorNavSection, RoleMenuLink[]> = { myWork, outputs, workspace };
  // Section order follows the first (highest-order) active role.
  const order = profiles[0]?.sectionOrder ?? BASELINE_PROFILE.sectionOrder;
  const sections = order
    .map((id) => ({ id, label: SECTION_LABELS[id], links: linksBySection[id] }))
    .filter((section) => section.links.length > 0);

  return { roleSlugs, sections, showProductSwitch: plansAvailable };
}
